import React from 'react';
import PropTypes from 'prop-types';
import { MemoryRouter, withRouter } from 'react-router-dom';
import { withLogger } from 'modules/shell/hoc/withLogger';
import Storage from '../localforage';
import BrowserHistoryContext from './BrowserHistoryContext';

/**
 * Proxy component is intended to be a child for MemoryRouter
 * It receives the in-memory history instance and attach event listener on history route changes
 * https://reactrouter.com/core/api/MemoryRouter/children-node
 */
const ProxyComponent = withRouter(({ history, children, onHistoryChange }) => {
  React.useEffect(() => {
    const unlistenHistory = history.listen(onHistoryChange.bind(null, history));

    // detach history listener
    // https://github.com/ReactTraining/history/blob/master/docs/api-reference.md#history.listen
    return unlistenHistory;
  }, []);

  return children;
});

export const resetMemoryRouterStorage = () => Storage.MEMORY_ROUTER.clear();

export const DEFAULT_MODULE_STATE = { index: 0, entries: ['/'] };
/**
 * Wrapper for React-Router MemoryRouter with the storing history location state into IndexedDB
 * So, it will be recovered on page refresh
 *
 * @returns {JSX.Element}
 * @constructor
 */
export const StatefulMemoryRouter = withRouter(({ history: browserHistory, module, children, logger }) => {
  const [loading, setLoading] = React.useState(true);
  const [routersState, setRoutersState] = React.useState({});

  const { state: browserHistoryState } = browserHistory.location;
  const moduleRouterState = routersState[module] || DEFAULT_MODULE_STATE;

  /**
   *
   * @param {string} actionName
   * @param {{}} error
   */
  const logError = (actionName, error) => {
    logger.error(`${Storage.MEMORY_ROUTER.name}: Failed to ${actionName}.`, {
      originService: 'stateful memory router',
      errorMessage: error?.message || 'An error has occurred',
    });
  };

  React.useEffect(() => {
    const getStorageState = async () => {
      setLoading(true);

      try {
        // Navigate user to specific in-memory location in case of `{initialEntry: '/path/to/somewhere?some_param=xxx'}` state
        // is provided in browser history instance
        if (browserHistory.action === 'PUSH' && browserHistoryState?.initialEntry) {
          const moduleState = {
            index: 1,
            entries: ['/', browserHistoryState.initialEntry],
          };

          try {
            await Storage.MEMORY_ROUTER.setItem(module, moduleState);

            // reset `initialEntry` state to prevent this logic to be executed on page refresh
            browserHistory.replace({
              state: { ...browserHistoryState.state, initialEntry: undefined },
            });
          } catch (e) {
            logError('set initial entry', e);
          }
        }

        const storageState = await Storage.MEMORY_ROUTER.getItems();

        if (storageState) {
          setRoutersState(storageState);
        }
      } catch (e) {
        logError('retrieve state', e);
      } finally {
        setLoading(false);
      }
    };

    getStorageState();
  }, []);

  React.useEffect(() => {
    // reset the in-memory history of current module in case of  `{resetEntries: true}` state is provided
    if (!loading && browserHistoryState?.resetEntries) {
      browserHistory.replace({
        state: { ...browserHistoryState, resetEntries: undefined },
      });

      // do not re-render the current module in case of it's already on the root location
      if (moduleRouterState.index > 0) {
        const moduleState = DEFAULT_MODULE_STATE;

        const resetEntries = async () => {
          setLoading(true);

          try {
            await Storage.MEMORY_ROUTER.setItem(module, moduleState);
            setRoutersState((state) => ({
              ...state,
              [module]: moduleState,
            }));
          } catch (e) {
            logError('reset entries', e);
          } finally {
            setLoading(false);
          }
        };

        resetEntries();
      }
    }
  }, [browserHistoryState, loading]);

  // Handle in-memory router state changes and save it into history state to the IndexedDB
  const handleHistoryChange = (history) => {
    const { index, entries } = history;
    const moduleState = { ...moduleRouterState, index, entries };

    Storage.MEMORY_ROUTER.setItem(module, moduleState)
      .then(() => {
        setRoutersState((state) => ({
          ...state,
          [module]: moduleState,
        }));
      })
      .catch((e) => {
        logError('update state', e);
      });
  };

  return (
    !loading && (
      <BrowserHistoryContext.Provider value={browserHistory}>
        <MemoryRouter initialEntries={moduleRouterState.entries} initialIndex={moduleRouterState.index}>
          <ProxyComponent onHistoryChange={handleHistoryChange}>{children}</ProxyComponent>
        </MemoryRouter>
      </BrowserHistoryContext.Provider>
    )
  );
});

StatefulMemoryRouter.propTypes = {
  module: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  logger: PropTypes.shape({}),
};

export default withLogger()(StatefulMemoryRouter);
